import { useEffect, useMemo, useState } from "react";
import { collection, onSnapshot, query, where, Timestamp, orderBy } from "firebase/firestore";
import { db } from "../lib/firebase";
import { useEffectiveTenantId } from "../lib/impersonation";

type Range = "today" | "week" | "month" | "custom";

type Purchase = {
  id: string;
  userId: string;
  amountFinal: number;
  status: string;
  createdAt: Date;
};

export function KpiScorecard() {
  const tenantId = useEffectiveTenantId();
  const [range, setRange] = useState<Range>("month");
  const [from, setFrom] = useState<string>(() => isoDaysAgo(30));
  const [to, setTo] = useState<string>(() => isoDaysAgo(0));
  const [purchases, setPurchases] = useState<Purchase[]>([]);

  const { fromDate, toDate } = useMemo(() => {
    if (range === "today") {
      const d = new Date(); d.setHours(0, 0, 0, 0);
      return { fromDate: d, toDate: new Date() };
    }
    if (range === "week") {
      const d = new Date(); d.setDate(d.getDate() - 7); d.setHours(0, 0, 0, 0);
      return { fromDate: d, toDate: new Date() };
    }
    if (range === "month") {
      const d = new Date(); d.setDate(d.getDate() - 30); d.setHours(0, 0, 0, 0);
      return { fromDate: d, toDate: new Date() };
    }
    return { fromDate: parseLocalDate(from, 0, 0, 0, 0), toDate: parseLocalDate(to, 23, 59, 59, 999) };
  }, [range, from, to]);

  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!tenantId) return;
    setLoading(true);
    setPurchases([]);
    const q = query(
      collection(db, "tenants", tenantId, "purchases"),
      where("createdAt", ">=", Timestamp.fromDate(fromDate)),
      where("createdAt", "<=", Timestamp.fromDate(toDate)),
      orderBy("createdAt", "desc")
    );
    return onSnapshot(q, (snap) => {
      setPurchases(
        snap.docs.map((d) => {
          const data = d.data();
          return {
            id: d.id,
            userId: data.userId,
            amountFinal: data.amountFinal ?? data.amount ?? 0,
            status: data.status ?? "approved",
            createdAt: (data.createdAt as Timestamp).toDate()
          };
        })
      );
      setLoading(false);
    });
  }, [tenantId, fromDate, toDate]);

  const valid = purchases.filter((p) => p.status === "approved");
  const totalIncome = valid.reduce((acc, p) => acc + p.amountFinal, 0);
  const uniqueCustomers = new Set(valid.map((p) => p.userId)).size;

  const newCustomers = useNewCustomersCount(tenantId, fromDate, toDate);

  const avgTicket = valid.length > 0 ? Math.round(totalIncome / valid.length) : 0;

  const retention = useRetentionRate(tenantId, fromDate);

  return (
    <section className="card flex flex-col gap-3">
      <div className="flex items-center justify-between">
        <h3 className="text-sm font-medium text-leaf-800">KPIs del comercio</h3>
        <select
          value={range}
          onChange={(e) => setRange(e.target.value as Range)}
          className="rounded-lg border border-leaf-200 bg-white px-2 py-1 text-xs"
        >
          <option value="today">Hoy</option>
          <option value="week">7 dias</option>
          <option value="month">30 dias</option>
          <option value="custom">Personalizado</option>
        </select>
      </div>

      {range === "custom" && (
        <div className="flex gap-2 text-xs">
          <input type="date" value={from} onChange={(e) => setFrom(e.target.value)} className="input flex-1" />
          <input type="date" value={to} onChange={(e) => setTo(e.target.value)} className="input flex-1" />
        </div>
      )}

      <div className="grid grid-cols-2 gap-2">
        <Kpi label="Ingresos" value={loading ? "…" : formatCLP(totalIncome)} />
        <Kpi label="Ventas" value={loading ? "…" : `${valid.length}`} />
        <Kpi label="Ticket promedio" value={loading ? "…" : formatCLP(avgTicket)} />
        <Kpi label="Clientes unicos" value={loading ? "…" : `${uniqueCustomers}`} />
        <Kpi label="Nuevos clientes" value={loading ? "…" : `${newCustomers}`} />
        <Kpi label="Retencion 30d" value={loading ? "…" : `${Math.round(retention * 100)}%`} />
      </div>
    </section>
  );
}

function Kpi({ label, value }: { label: string; value: string }) {
  return (
    <div className="rounded-xl bg-leaf-50 p-3">
      <p className="text-[10px] uppercase tracking-wider text-leaf-600">{label}</p>
      <p className="mt-1 font-display text-lg font-semibold text-leaf-950">{value}</p>
    </div>
  );
}

function useNewCustomersCount(tenantId: string | null | undefined, from: Date, to: Date): number {
  const [count, setCount] = useState(0);
  useEffect(() => {
    if (!tenantId) return;
    const q = query(
      collection(db, "tenants", tenantId, "users"),
      where("role", "==", "customer"),
      where("createdAt", ">=", Timestamp.fromDate(from)),
      where("createdAt", "<=", Timestamp.fromDate(to))
    );
    return onSnapshot(
      q,
      (snap) => setCount(snap.size),
      () => setCount(0)
    );
  }, [tenantId, from, to]);
  return count;
}

function useRetentionRate(tenantId: string | null | undefined, since: Date): number {
  const [rate, setRate] = useState(0);
  useEffect(() => {
    if (!tenantId) return;
    const q = query(
      collection(db, "tenants", tenantId, "purchases"),
      where("createdAt", ">=", Timestamp.fromDate(since)),
      where("status", "==", "approved")
    );
    return onSnapshot(q, (snap) => {
      const counts = new Map<string, number>();
      snap.docs.forEach((d) => {
        const uid = d.data().userId as string;
        counts.set(uid, (counts.get(uid) ?? 0) + 1);
      });
      const total = counts.size;
      const returning = Array.from(counts.values()).filter((c) => c >= 2).length;
      setRate(total > 0 ? returning / total : 0);
    });
  }, [tenantId, since]);
  return rate;
}

function formatCLP(n: number): string {
  return new Intl.NumberFormat("es-CL", {
    style: "currency",
    currency: "CLP",
    maximumFractionDigits: 0
  }).format(n);
}

function isoDaysAgo(days: number): string {
  const d = new Date();
  d.setDate(d.getDate() - days);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}

function parseLocalDate(s: string, h: number, m: number, sec: number, ms: number): Date {
  const [y, mo, d] = s.split("-").map(Number);
  return new Date(y, mo - 1, d, h, m, sec, ms);
}
